import {
  ACCEPTED_FILE_EXTENSIONS,
  MAX_FILE_SIZE,
} from '@client/pages/payroll/constants/fileProperties';
import {FileForUpload} from '@client/pages/payroll/types';
import {fileToBase64} from '@common-web/lib/fileToBase64';

const PAYROLL_FILE_PREFIXES = ['P_', 'Payroll_', 'PR_'];

/**
 * Common payroll file prefixes come first, then alphabetically.
 * Payroll files are prioritized for column mapping suggestions, since they usually contain the
 * best column to use for multi-file uploads
 */
function compareFileNames(a: string, b: string): number {
  const aPayroll = PAYROLL_FILE_PREFIXES.some((prefix) => a.startsWith(prefix));
  const bPayroll = PAYROLL_FILE_PREFIXES.some((prefix) => b.startsWith(prefix));
  if (aPayroll && !bPayroll) return -1;
  if (!aPayroll && bPayroll) return 1;
  return a.localeCompare(b);
}

export function validateFile(file: File): string | undefined {
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!ACCEPTED_FILE_EXTENSIONS.includes(extension)) {
    return `File type ${extension} not supported. Please use ${ACCEPTED_FILE_EXTENSIONS.join(', ')}.`;
  }

  if (file.size > MAX_FILE_SIZE) {
    return 'File size must be less than 50MB.';
  }

  return undefined;
}

export function guessFileType(name: string): 'CSV' | 'XLSX' | 'XLS' {
  const extension = name.toLowerCase().split('.').pop();
  if (extension === 'xlsx') {
    return 'XLSX';
  } else if (extension === 'xls') {
    return 'XLS';
  } else {
    return 'CSV';
  }
}

export function formatFilesForUpload(
  selectedFiles: FileList | File[],
  fileIdStartIndex = 0,
): FileForUpload[] {
  return [...Array.from(selectedFiles)]
    .sort((a, b) => compareFileNames(a.name, b.name))
    .map((file, index) => ({
      file,
      fileId: `file-${fileIdStartIndex + index + 1}`,
      hasHeaders: true,
      delimiter: guessFileType(file.name) === 'CSV' ? ',' : undefined,
      error: validateFile(file),
      isPrimary: false,
    }));
}

export function sortAndReassignFileIds(files: FileForUpload[]): FileForUpload[] {
  return [...files]
    .sort((a, b) => compareFileNames(a.file.name, b.file.name))
    .map((file, index) => ({
      ...file,
      fileId: `file-${index + 1}`,
    }));
}

export async function formatFilesForProcessing(files: FileForUpload[]) {
  return await Promise.all(
    files.map(async (f) => ({
      fileName: f.file.name,
      fileContent: await fileToBase64(f.file),
      fileType: guessFileType(f.file.name),
      fileId: f.fileId,
      delimiter: f.delimiter,
      hasHeaders: f.hasHeaders,
    })),
  );
}
